import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState
} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {InputMask} from '@react-input/mask'

import {cssTheme} from '../../../themes'
import {Badge} from '../Badge'
import {
  FS_PRIVACY_MASK_CLASS,
  getClassName,
  InputTextType,
  TextTransform
} from '../helpers/InputBase'
import {
  CaptionStyles,
  FieldStyles,
  FieldWrapperStyles,
  InputGroupStyles,
  InputLabel,
  InputLabelText,
  InputTextStyles
} from './styles'

interface Props {
  /** The autoComplete attribute for the input */
  autoComplete?: string
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Caption message to display below the input */
  caption?: ReactNode
  /** Used to identify the input */
  dataId?: string
  /** An object of custom props that is not explicitly supported, but might be required for some custom implementations.
   * This can also be used to override the explicit props, like a custom onChange function.
   * or as a way to get rid of the chrome autofill.
   * */
  // eslint-disable-next-line
  customProps?: Record<string, any>
  /** Used to link the input with the label when the label is clicked */
  id: string
  /** Put the input in the disabled state */
  disabled?: boolean
  /** Put the input in the readonly state */
  isReadOnly?: boolean
  /** Add an asterisk to the label */
  isRequired?: boolean
  /** Put the input in the valid state */
  isValid?: boolean
  /** Put the input in the invalid state */
  isInvalid?: boolean
  /** Whether the input has sensitive data and should be masked in FullStory */
  isPrivate?: boolean
  /** Label text that appears above the input field. Will always be displayed instead of placeholder if both are provided */
  label?: string
  /** The mask used to format the user input */
  mask?: string
  /** The name of the input */
  name: string
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  /** Callback function that is called when the input value changes */
  onChange?: (value: string, event: ChangeEvent) => void
  /** Callback function that is called when the input is focused */
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  /** Display a placeholder when the input is empty and the label is not provided. */
  placeholder?: string
  /** Any component to display before the input */
  prefix?: ReactNode
  /** Any component to display after the input */
  suffix?: ReactNode
  /** CSS text-transform style to apply to the input field */
  textTransformType?: TextTransform
  /** The type of the input */
  type?: InputTextType
  /** The value of the input */
  value?: string
}

const Field = styled.label<{prefixWidth: number}>`
  &:focus-within,
  &.--is-filled {
    /* Dynamically position the label when the prefix is present */
    &.--has-prefix .label {
      transform: ${({prefixWidth}) =>
        `translateX(${`-${prefixWidth + 10}px`}) translateY(-18px)`};
    }
  }

  &.--has-label:not(:focus-within) input::placeholder {
    opacity: 0;
  }
`

const Input = styled.input<{
  textTransformType?: TextTransform
}>`
  text-transform: ${({textTransformType}) =>
    textTransformType ? textTransformType : TextTransform.none};
`
const InputWithRef = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => <Input {...props} ref={ref} />)
InputWithRef.displayName = 'InputWithRef'

const PrefixWrapperStyles = styled.div`
  color: ${cssTheme.colors.input.default.icon};
`

export const InputText = forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      name,
      type = InputTextType.text,
      autoComplete,
      className,
      dataId,
      label,
      placeholder,
      value,
      caption,
      prefix,
      suffix,
      isValid = false,
      isInvalid = false,
      disabled = false,
      isRequired = false,
      isReadOnly = false,
      isPrivate = false,
      onChange,
      onFocus,
      onBlur,
      textTransformType,
      mask,
      customProps
    }: Props,
    ref: Ref<HTMLInputElement>
  ) => {
    const [isFilled, setIsFilled] = useState(false)
    const [prefixWidth, setPrefixWidth] = useState(0)
    const prefixRef = useRef<HTMLDivElement>(null)

    const handleChange = (event: ChangeEvent) => {
      const {value: newValue} = event.currentTarget as HTMLInputElement
      onChange?.(newValue, event)
    }

    useEffect(() => {
      setIsFilled(Boolean(value?.length))
    }, [value])

    useEffect(() => {
      if (prefix && prefixRef.current) {
        setPrefixWidth(prefixRef.current.offsetWidth)
      }
    }, [prefix, prefixRef?.current?.offsetHeight])

    const commonProps = {
      autoComplete,
      id,
      disabled,
      name,
      textTransformType,
      type,
      value,
      placeholder,
      label,
      onBlur,
      onFocus,
      onChange: handleChange,
      readOnly: isReadOnly,
      ['data-id']: dataId,
      ['data-field-error']: isInvalid
    }

    return (
      <div css={FieldWrapperStyles} className={className}>
        <Field
          prefixWidth={prefixWidth}
          htmlFor={id}
          css={FieldStyles}
          className={cx(
            isInvalid && '--is-invalid',
            isFilled && '--is-filled',
            disabled && '--is-disabled',
            isReadOnly && '--is-read-only',
            !!prefix && '--has-prefix',
            label && '--has-label'
          )}
        >
          {prefix && (
            <PrefixWrapperStyles ref={prefixRef}>{prefix}</PrefixWrapperStyles>
          )}

          <div css={InputGroupStyles}>
            {mask ? (
              <InputMask
                ref={ref}
                mask={mask}
                {...customProps}
                {...commonProps}
                css={InputTextStyles}
                className={cx(getClassName(isPrivate))}
                replacement={{_: /\d/}}
                component={InputWithRef}
              />
            ) : (
              <Input
                ref={ref}
                {...commonProps}
                {...customProps}
                css={InputTextStyles}
                className={cx(isPrivate && FS_PRIVACY_MASK_CLASS)}
              />
            )}
            {label && (
              <InputLabel className="label">
                <InputLabelText>{label}</InputLabelText>
                {isRequired && <span className="asterisk">*</span>}
              </InputLabel>
            )}
          </div>

          {isValid && (
            <Badge size="xs" variant="success" iconName="Checkmark" />
          )}

          {isInvalid && (
            <Badge size="xs" variant="danger" iconName="WarningMark" />
          )}

          {suffix && <div>{suffix}</div>}
        </Field>
        {caption && (
          <div
            css={CaptionStyles}
            className={cx(
              isInvalid && '--is-invalid',
              disabled && '--is-disabled'
            )}
          >
            {caption}
          </div>
        )}
      </div>
    )
  }
)

InputText.displayName = 'InputText'
